import React from 'react';
import { Action } from 'redux';
import { initializeState } from 'src/utils/Initial';
import { isType } from 'typescript-fsa';
import * as Actions from '../actions/eLearning';

export interface IELearningParamMap {
	completion?: number;
	markTimingFlg?: number;
	orderFlg?: number;
}

/**
 * eラーニングメニューのState型
 */
export interface IELearningState {
	readonly isOpenIndexTree: boolean;
	readonly careerTitle: string;
	readonly currentPage: number;
	readonly viewMode: string;
	readonly mediaSiteApiStatus: string;
	readonly indexTree: Actions.IIndexTreeItem[];
	/** 現在表示中のindexTreeItem */
	readonly currentTreeItem?: Actions.IIndexTreeItem | null;
	/** 保留中のindexTreeItem */
	readonly reservedTreeItem?: Actions.IIndexTreeItem;
	readonly progress: number;
	/** 受講画面が読み込みの状態表示制御フラグ */
	readonly progressLoadingFlg: boolean;
	readonly explanationPage: number;
	readonly resumeTopic: string;
	readonly resumePage: number;
	readonly startTime: string;
	readonly isOpenHint: boolean;
	readonly documentName: string;
	readonly tsqId: string;
	readonly status: number;
	readonly testTitle: string;
	readonly retries: string;
	readonly cnt: string;
	readonly timeLimit: string;
	readonly showFinishDialog: boolean;
	readonly elearingUpdateResult?: Actions.IScoringButtonClickSaveResult;
	readonly mapParam: IELearningParamMap;
	readonly isFirst: number;
	readonly showTree: boolean;
	readonly lastLocation: string | null;
	readonly scorm: boolean;
	readonly scormApi: React.RefObject<HTMLIFrameElement>;
	readonly markAfter: boolean;
	readonly notMarkCompleteFlg: boolean;
	/** テキストAdmintTV連携フラグ 0:連携しない 1:連携する */
	readonly admintFlg: string;
	/** テキストAdmintTV連携視聴画面URL */
	readonly admintUrl: string;
	/** 次へナビゲーション制御フラグ false: 活性　true:非活性 */
	readonly nextBtnDisFlg: boolean;
	/** eラーニングのコンテンツの必須要素の有無フラグ false:無し true:有り */
	readonly requiredItemFlg: boolean;
	/** テキストAIロープレフラグ 0:AIロープレコンテンツ以外 1:AIロープレコンテンツ */
	readonly aiRolePlayFlg: string;
	/** テキストAIロープレURL */
	readonly aiRolePlayUrl: string;
	/** テキストAIロープレ結果取得レスポンスコード */
	readonly aiRolePlayResponseCode: number;
	/** テキストAIロープレ結果取得エラーメッセージ */
	readonly aiRolePlayErrMessage: string;
}

/**
 * eラーニングメニューのState初期値
 */
const initialState: IELearningState = {
	admintFlg: '0',
	admintUrl: '',
	aiRolePlayErrMessage: '',
	aiRolePlayFlg: '',
	aiRolePlayResponseCode: 0,
	aiRolePlayUrl: '',
	careerTitle: '',
	cnt: '',
	currentPage: 1,
	currentTreeItem: undefined,
	documentName: '',
	explanationPage: 0,
	indexTree: [],
	isFirst: -1,
	isOpenHint: false,
	isOpenIndexTree: false,
	lastLocation: null,
	mapParam: {},
	markAfter: false,
	mediaSiteApiStatus: 'initial',
	notMarkCompleteFlg: false,
	progress: 0,
	requiredItemFlg: false,
	resumePage: 0,
	resumeTopic: '',
	retries: '',
	scorm: false,
	scormApi: React.createRef(),
	showFinishDialog: false,
	showTree: true,
	startTime: '',
	status: 0,
	testTitle: '',
	timeLimit: '',
	tsqId: '',
	viewMode: '0',

	elearingUpdateResult: undefined,

	nextBtnDisFlg: false,
	progressLoadingFlg: false,
};

/**
 * eラーニングメニュー用reducer
 * @param state State
 * @param action アクション
 */
const eLearning = (state: IELearningState, action: Action) => {
	if (!state) {
		// State初期化処理
		state = initialState;
		state = initializeState(state);
	}
	if (isType(action, Actions.openELearningIndexTree)) {
		return {
			...state,
			isOpenIndexTree: action.payload.isOpen,
		};
	}

	if (isType(action, Actions.setELearningTopBlock)) {
		return {
			...state,
			careerID: action.payload.careerID,
			classID: action.payload.classID,
			cnt: action.payload.cnt,
			explanationPage: action.payload.explanationPage,
			lang: action.payload.lang,
			lastLocation: action.payload.lastLocation,
			resumePage: action.payload.resumePage,
			resumeTopic: action.payload.resumeTopic,
			retries: action.payload.retries,
			scorm: action.payload.scorm,
			status: action.payload.status,
			testTitle: action.payload.testTitle,
			timeLimit: action.payload.timeLimit,
			tsqId: action.payload.tsqId,
		};
	}

	if (isType(action, Actions.setELearningProgressBlock)) {
		return {
			...state,
			mapParam: {
				...state.mapParam,
				completion: action.payload.completion,
			},
			progress: action.payload.progress,
		};
	}

	if (isType(action, Actions.setELearningTreeBlock)) {
		let newState: IELearningState = state;
		let value: number = state.isFirst;
		if (value !== 2 && value !== 3) {
			const checkIsFirst: boolean = checkIsFirstTime(
				action.payload.indexTree
			);
			if (
				checkIsFirst &&
				(state.resumeTopic === null || state.resumeTopic === '')
			) {
				value = 1;
			} else {
				value = 0;
			}
		}

		const mapParam = {
			...state.mapParam,
			...action.payload.mapParam,
		};

		if (state.currentTreeItem && !action.payload.preview) {
			const currentTreeItem = findIndexTree(
				action.payload.indexTree,
				state.currentTreeItem.id
			);
			newState = {
				...state,
				careerTitle: action.payload.careerTitle,
				currentTreeItem,
				documentName: action.payload.documentName,
				indexTree: action.payload.indexTree,
				isFirst: value,
				mapParam,
				markAfter: action.payload.markAfter,
				requiredItemFlg: action.payload.requiredItemFlg,
			};
		} else if (
			state.currentTreeItem === undefined &&
			!action.payload.preview
		) {
			let reservedTreeItem;
			let currentTreeItem;
			if (value === 1 || value === 2) {
				reservedTreeItem = undefined;
				currentTreeItem = undefined;
			}
			newState = {
				...state,
				careerTitle: action.payload.careerTitle,
				currentPage: 1,
				currentTreeItem,
				documentName: action.payload.documentName,
				indexTree: action.payload.indexTree,
				isFirst: value,
				mapParam,
				markAfter: action.payload.markAfter,
				requiredItemFlg: action.payload.requiredItemFlg,
				reservedTreeItem,
			};
		} else {
			newState = {
				...state,
				careerTitle: action.payload.careerTitle,
				documentName: action.payload.documentName,
				indexTree: action.payload.indexTree,
				isFirst: value,
				mapParam,
				markAfter: action.payload.markAfter,
				requiredItemFlg: action.payload.requiredItemFlg,
			};
		}
		return newState;
	}

	if (isType(action, Actions.setELearningTextBlock)) {
		return {
			...state,
			startTime: action.payload.startTime,
		};
	}

	if (isType(action, Actions.setELearningTreeItem)) {
		return {
			...state,
			currentPage: 1,
			currentTreeItem: action.payload.item,
		};
	}

	if (isType(action, Actions.setELearningReservedTreeItem)) {
		return {
			...state,
			reservedTreeItem: action.payload.item,
		};
	}

	if (isType(action, Actions.issueELearningReservedTreeItem)) {
		return {
			...state,
			currentPage: 1,
			currentTreeItem: state.reservedTreeItem,
			reservedTreeItem: undefined,
		};
	}

	if (isType(action, Actions.clearELearningReservedTreeItem)) {
		return {
			...state,
			currentTreeItem: undefined,
			reservedTreeItem: undefined,
		};
	}

	if (isType(action, Actions.setELearningPage)) {
		return {
			...state,
			currentPage: action.payload.page,
		};
	}

	if (isType(action, Actions.clearELearning)) {
		return { ...initialState };
	}

	if (isType(action, Actions.openELearningHint)) {
		return {
			...state,
			isOpenHint: action.payload.isOpen,
		};
	}

	if (isType(action, Actions.setELearningItemIndex)) {
		return {
			...state,
			currentIndex: action.payload.index,
		};
	}

	if (isType(action, Actions.setELearningNextOrTopITem)) {
		const newState = { ...state };
		const serialized = serializeIndexTree(newState.indexTree);
		let current: number;
		for (current = 0; current < serialized.length - 1; current++) {
			if (serialized[current] === newState.currentTreeItem) {
				break;
			}
		}
		if (current < serialized.length - 1) {
			newState.currentTreeItem = serialized[current + 1];
		} else {
			newState.currentTreeItem = findFirstItem(newState.indexTree);
		}
		return newState;
	}

	if (isType(action, Actions.showELearningByBookmark)) {
		let currentTreeItem;
		currentTreeItem = findIndexTree(
			state.indexTree,
			action.payload.topicID
		);
		let currentPage = action.payload.page;
		if (currentTreeItem === undefined) {
			currentTreeItem = findFirstItem(state.indexTree);
			currentPage = 1;
		}
		let reservedTreeItem;
		if (
			currentTreeItem &&
			currentTreeItem.kind === Actions.ELearningKind.TEST
		) {
			reservedTreeItem = currentTreeItem;
			currentTreeItem = undefined;
		}

		return {
			...state,
			currentPage,
			currentTreeItem,
			reservedTreeItem,
		};
	}

	if (isType(action, Actions.setELearningTreeItemTestLog)) {
		const currentTreeItem = findTestLog(
			state.indexTree,
			action.payload.testID,
			action.payload.thistoryID,
			action.payload.displayStyle
		);

		if (currentTreeItem) {
			return {
				...state,
				currentPage: 1,
				currentTreeItem,
			};
		}
	}

	if (isType(action, Actions.resetELearningPage)) {
		const newState = {
			...state,
			isOpenIndexTree: true,
		};
		if (newState.indexTree.length > 0) {
			// currentIndexを初期化
			newState.currentTreeItem = findFirstItem(newState.indexTree);
			newState.currentPage = 1;
		}
		return newState;
	}

	if (isType(action, Actions.showELearningFinishDialog)) {
		return {
			...state,
			showFinishDialog: action.payload.isOpen,
		};
	}

	if (isType(action, Actions.getScoringButtonClickResult)) {
		return {
			...state,
			elearingUpdateResult: action.payload.elearingUpdateResult,
		};
	}

	if (isType(action, Actions.setShowFirstTimeDialog)) {
		return {
			...state,
			isFirst: action.payload.isFirst,
		};
	}

	if (isType(action, Actions.setShowTree)) {
		return {
			...state,
			showTree: action.payload.showTree,
		};
	}

	if (isType(action, Actions.setScormApi)) {
		return {
			...state,
			scormApi: action.payload.scormApi,
		};
	}

	if (isType(action, Actions.clearElearingUpdateResult)) {
		return {
			...state,
			elearingUpdateResult: undefined,
			notMarkCompleteFlg: true,
		};
	}

	if (isType(action, Actions.setNotMarkCompleteFlg)) {
		return {
			...state,
			notMarkCompleteFlg: false,
		};
	}

	if (isType(action, Actions.setELearningTextAdmintResult)) {
		return {
			...state,
			admintFlg: action.payload.admintFlg,
			admintUrl: action.payload.admintUrl,
		};
	}

	if (isType(action, Actions.setAdmintFlg)) {
		return {
			...state,
			admintFlg: action.payload.admintFlg,
		};
	}

	if (isType(action, Actions.setNextBtnDisFlg)) {
		return {
			...state,
			nextBtnDisFlg: action.payload.nextBtnDisFlg,
		};
	}

	if (isType(action, Actions.setProgressLoadingFlg)) {
		return {
			...state,
			progressLoadingFlg: action.payload.progressLoadingFlg,
		};
	}

	if (isType(action, Actions.setElearningTextAiRolePlayResult)) {
		return {
			...state,
			aiRolePlayFlg: action.payload.aiRolePlayFlg,
			aiRolePlayUrl: action.payload.aiRolePlayUrl,
		};
	}

	if (isType(action, Actions.setAiRolePlayFlg)) {
		return {
			...state,
			aiRolePlayFlg: action.payload.aiRolePlayFlg,
		};
	}

	if (isType(action, Actions.setElearningTextAiRolePlayResponse)) {
		return {
			...state,
			aiRolePlayErrMessage: action.payload.aiRolePlayErrMessage,
			aiRolePlayResponseCode: action.payload.aiRolePlayResponseCode,
		};
	}

	return state;
};

const checkIsFirstTime = (indexTree: Actions.IIndexTreeItem[]): boolean => {
	for (const item of indexTree) {
		if (
			(item.iconClass && item.iconClass.search(/\-complete/) > -1) ||
			item.kind === 6 ||
			item.kind === 9
		) {
			return false;
		}
		if (item.children) {
			const isFirstTime = checkIsFirstTime(item.children);
			if (!isFirstTime) {
				return isFirstTime;
			}
		}
	}
	return true;
};

const findIndexTree = (
	indexTree: Actions.IIndexTreeItem[],
	id: string
): Actions.IIndexTreeItem | undefined => {
	for (const item of indexTree) {
		if (item.id.indexOf(id) > -1) {
			return item;
		}
		if (item.children) {
			const child = findIndexTree(item.children, id);
			if (child) {
				return child;
			}
		}
	}
	return undefined;
};

const findFirstItem = (
	indexTree: Actions.IIndexTreeItem[]
): Actions.IIndexTreeItem | undefined => {
	for (const item of indexTree) {
		if (item.children) {
			const child = findFirstItem(item.children);
			if (child) {
				return child;
			}
		}
		if (
			item.kind === Actions.ELearningKind.OUTLINE ||
			item.kind === Actions.ELearningKind.TEXT ||
			item.kind === Actions.ELearningKind.TEST ||
			item.kind === Actions.ELearningKind.SCORM ||
			item.kind === Actions.ELearningKind.ENQUETE ||
			item.kind === Actions.ELearningKind.SUBJECT
		) {
			if (
				(item.kind === Actions.ELearningKind.ENQUETE ||
					item.kind === Actions.ELearningKind.SUBJECT) &&
				item.expired
			) {
				continue;
			}
			return item;
		}
	}
	return undefined;
};

const findIndexTreeTestLog = (
	indexTree: Actions.IIndexTreeItem[],
	testID: string,
	thistoryID: number
): Actions.IIndexTreeItem | undefined => {
	for (const item of indexTree) {
		if (item.testID === testID && +item.id.substr(2) === thistoryID) {
			return item;
		}
		if (item.children) {
			const child = findIndexTreeTestLog(
				item.children,
				testID,
				thistoryID
			);
			if (child) {
				return child;
			}
		}
	}
	return undefined;
};

const findTestLog = (
	indexTree: Actions.IIndexTreeItem[],
	testID: string,
	thistoryID: number,
	displayStyle: number
): Actions.IIndexTreeItem | undefined => {
	const currentTreeItem = findIndexTreeTestLog(indexTree, testID, thistoryID);

	if (currentTreeItem) {
		return currentTreeItem;
	} else {
		// 見つからないときは作成する。閲覧モード時のみあり得る。
		const dummy: Actions.IIndexTreeItem = {
			answerPeriod: '',
			children: null,
			cnt: 0,
			completeFlg: false,
			disableFlg: false,
			displayStyle,
			enqueteType: '1',
			expired: false,
			hintList: null,
			iconClass: '',
			id: 'TH' + thistoryID,
			indexNo: 0,
			kind: Actions.ELearningKind.TESTLOG,
			notMarkFlg: false,
			page: 0,
			pageCount: 0,
			requiredFlg: false,
			retries: 0,
			scoType: null,
			seqNo: 0,
			testID,
			testTitle: null,
			text: '',
			timeLimit: 0,
			userBookMark: 0,
		};
		return dummy;
	}
};

// インデックスツリーをナビゲーション順に並べる
const serializeIndexTree = (src: Actions.IIndexTreeItem[]) => {
	const serialized = [] as Actions.IIndexTreeItem[];
	for (const item of src) {
		if (
			item.kind === Actions.ELearningKind.TEXT ||
			item.kind === Actions.ELearningKind.TEST
		) {
			serialized.push(item);
		}
		if (item.children) {
			for (const child of serializeIndexTree(item.children)) {
				serialized.push(child);
			}
		}
	}
	return serialized;
};

export default eLearning;



// WEBPACK FOOTER //
// ./src/reducers/eLearning.ts